package com.bulut.mecitogullari.ui.excel

import com.bulut.mecitogullari.core.importer.ExcelImport
import com.bulut.mecitogullari.core.importer.ExcelImportPreview
import com.bulut.mecitogullari.core.i18n.Localization
import com.bulut.mecitogullari.core.i18n.fillTemplate
import com.bulut.mecitogullari.core.platform.pickExcelFile
import com.bulut.mecitogullari.data.repository.ProjectRepository
import com.bulut.mecitogullari.data.repository.TransactionRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch

/** State machine of the .xlsx import dialog. */
sealed interface ExcelImportState {
    data object Hidden : ExcelImportState

    data class Preview(
        val preview: ExcelImportPreview,
        /** Set on proje bazlı import: every row goes to this project. */
        val targetProjectName: String?,
    ) : ExcelImportState

    data object Importing : ExcelImportState
    data class Done(val transactionCount: Int) : ExcelImportState
    data class Failed(val message: String) : ExcelImportState
}

/** Shared pick→parse→preview→write flow for the two import entry points
 *  (İşlemler = ay bazlı, Proje detayı = proje bazlı). */
class ExcelImportController(
    private val scope: CoroutineScope,
    private val transactionRepository: TransactionRepository,
    private val projectRepository: ProjectRepository,
) {

    private val _state = MutableStateFlow<ExcelImportState>(ExcelImportState.Hidden)
    val state: StateFlow<ExcelImportState> = _state.asStateFlow()

    /**
     * Opens the file picker and shows the preview dialog.
     * [fixedProjectId] non-null = proje bazlı: all rows go to that project.
     * [defaultDateEpochMillis] is used for rows without a date.
     */
    fun start(fixedProjectId: String?, fixedProjectName: String?, defaultDateEpochMillis: Long) {
        pickExcelFile { rows ->
            if (rows == null) return@pickExcelFile
            scope.launch {
                val preview = ExcelImport.parse(
                    rows = rows,
                    projects = projectRepository.projects.first(),
                    fixedProjectId = fixedProjectId,
                    defaultDateEpochMillis = defaultDateEpochMillis,
                )
                _state.value = if (preview.isEmpty) {
                    ExcelImportState.Failed(
                        buildString {
                            val s = Localization.strings
                            append(s.importNoRows)
                            if (preview.skippedSaleCount > 0) {
                                append("\n\n")
                                append(
                                    s.importSalesSkippedNote
                                        .fillTemplate("count" to preview.skippedSaleCount.toString())
                                )
                            }
                            if (preview.errors.isNotEmpty()) {
                                append("\n\n")
                                append(
                                    s.importSkippedRows
                                        .fillTemplate("count" to preview.errors.size.toString()) + "\n"
                                )
                                append(preview.errors.take(5).joinToString("\n"))
                                if (preview.errors.size > 5) append("\n…")
                            }
                        }
                    )
                } else {
                    ExcelImportState.Preview(preview, fixedProjectName)
                }
            }
        }
    }

    fun confirm() {
        val preview = (_state.value as? ExcelImportState.Preview)?.preview ?: return
        _state.value = ExcelImportState.Importing
        scope.launch {
            runCatching {
                preview.transactions.forEach { transactionRepository.upsert(it) }
            }.onSuccess {
                _state.value = ExcelImportState.Done(preview.transactions.size)
            }.onFailure { error ->
                _state.value = ExcelImportState.Failed(
                    Localization.strings.importFailedMessage.fillTemplate(
                        "error" to (error.message ?: Localization.strings.unknownError)
                    )
                )
            }
        }
    }

    fun dismiss() {
        _state.value = ExcelImportState.Hidden
    }
}
